import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPurchaseOrderFormInitialValues } from '../data/purchaseOrderConstants';
import {
  createPurchaseOrder,
  createSupplierBalancing,
  deleteSupplierBalancing,
  fetchInitialSummaryPurchaseOrders,
  fetchPurchaseOrders,
  fetchSupplierBalancings,
  updatePurchaseOrder,
  updateSupplierBalancingStatus,
} from '../services/purchaseOrderService';
import { fetchLansas } from '../services/lansaService';
import { subscribeToPurchaseOrderChanges } from '../services/purchaseOrderRealtimeService';
import { fetchSeaweedTypes } from '../services/seaweedTypeService';
import { fetchSuppliers } from '../services/supplierService';
import { fetchWarehouseLocations } from '../services/warehouseLocationService';
import {
  buildPurchaseOrderEditValues,
  getInitialPurchaseOrderActiveView,
  getSupplierSacksMark,
  normalizePurchaseOrderPayload,
  resetPurchaseOrderFormState,
  validatePurchaseOrderForm,
} from './purchase-orders/purchaseOrderHelpers';
import usePurchaseOrderEdit from './purchase-orders/usePurchaseOrderEdit';
import usePurchaseOrderHistory from './purchase-orders/usePurchaseOrderHistory';
import usePurchaseOrderProgress from './purchase-orders/usePurchaseOrderProgress';

const PURCHASE_ORDER_PAGE_SIZE = 10;
const DEFAULT_PURCHASE_ORDER_STATUS_FILTER = 'Active';

function usePurchaseOrders(initialActiveView = getInitialPurchaseOrderActiveView()) {
  const [activeView, setActiveView] = useState(initialActiveView);
  const [filterValues, setFilterValues] = useState(createInitialPurchaseOrderFilterValues);
  const [appliedFilters, setAppliedFilters] = useState(createInitialPurchaseOrderFilterValues);
  const [currentPage, setCurrentPage] = useState(1);
  const [deliveryDateSort, setDeliveryDateSort] = useState('default');
  const [formValues, setFormValues] = useState(createPurchaseOrderFormInitialValues);
  const [formErrors, setFormErrors] = useState({});
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [initialSummaryPurchaseOrders, setInitialSummaryPurchaseOrders] = useState([]);
  const [supplierBalancings, setSupplierBalancings] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [seaweedTypeOptions, setSeaweedTypeOptions] = useState([]);
  const [warehouseLocationOptions, setWarehouseLocationOptions] = useState([]);
  const [lansaOptions, setLansaOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [cancelingPurchaseOrderId, setCancelingPurchaseOrderId] = useState(null);

  const purchaseOrderEdit = usePurchaseOrderEdit({
    supplierOptions,
    setPurchaseOrders,
    setSuccessMessage,
  });
  const purchaseOrderProgress = usePurchaseOrderProgress({
    purchaseOrders,
    setPurchaseOrders,
    setSuccessMessage,
  });
  const purchaseOrderHistory = usePurchaseOrderHistory();

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setSuccessMessage('');
    }, 3500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [successMessage]);

  const loadPurchaseOrderPageData = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }

    setLoadError('');

    try {
      const [
        ordersResponse,
        initialSummariesResponse,
        supplierBalancingsResponse,
        suppliersResponse,
        lansasResponse,
        seaweedTypesResponse,
        warehouseLocationsResponse,
      ] = await Promise.all([
        fetchPurchaseOrders(),
        fetchInitialSummaryPurchaseOrders(),
        fetchSupplierBalancings(),
        fetchSuppliers(),
        fetchLansas(),
        fetchSeaweedTypes(),
        fetchWarehouseLocations(),
      ]);

      setPurchaseOrders(dedupePurchaseOrders(ordersResponse));
      setInitialSummaryPurchaseOrders(initialSummariesResponse);
      setSupplierBalancings(supplierBalancingsResponse);
      setSupplierOptions(
        suppliersResponse.map((supplier) => ({
          value: String(supplier.id),
          label: supplier.companyName,
          sacksMark: supplier.sacksMark || '',
          status: supplier.status || '',
        }))
      );
      setLansaOptions(
        lansasResponse.map((lansa) => ({
          value: lansa.name,
          label: lansa.name,
        }))
      );
      setSeaweedTypeOptions(
        seaweedTypesResponse.map((seaweedType) => ({
          value: seaweedType.name,
          label: seaweedType.name,
          description: seaweedType.description || '',
        }))
      );
      setWarehouseLocationOptions(
        warehouseLocationsResponse.map((warehouseLocation) => ({
          value: warehouseLocation.name,
          label: warehouseLocation.name,
          branchName: warehouseLocation.branchName || '',
          description: warehouseLocation.branchName
            ? `Branch: ${warehouseLocation.branchName}`
            : '',
        }))
      );
    } catch (error) {
      setLoadError(error.message);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadPurchaseOrderPageData();

    function handleWindowFocus() {
      loadPurchaseOrderPageData(false);
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        loadPurchaseOrderPageData(false);
      }
    }

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadPurchaseOrderPageData]);

  useEffect(() => {
    return subscribeToPurchaseOrderChanges(() => {
      loadPurchaseOrderPageData(false);
    });
  }, [loadPurchaseOrderPageData]);

  const activeSupplierOptions = useMemo(
    () =>
      supplierOptions
        .filter((supplierOption) => supplierOption.status === 'Active')
        .sort((firstSupplier, secondSupplier) =>
          firstSupplier.label.localeCompare(secondSupplier.label)
        ),
    [supplierOptions]
  );
  const hasSuppliers = activeSupplierOptions.length > 0;
  const hasSeaweedTypes = seaweedTypeOptions.length > 0;
  const hasWarehouseLocations = warehouseLocationOptions.length > 0;
  const hasLansas = lansaOptions.length > 0;

  const filteredPurchaseOrders = useMemo(() => {
    const normalizedSearch = appliedFilters.search.trim().toLowerCase();

    return purchaseOrders.filter((purchaseOrder) => {
      const matchesSearch =
        !normalizedSearch ||
        getPurchaseOrderSearchableCode(purchaseOrder)
          .toLowerCase()
          .includes(normalizedSearch) ||
        String(purchaseOrder.supplierName || '').toLowerCase().includes(normalizedSearch);
      const matchesPoDate =
        !appliedFilters.poDate ||
        toDateInputValue(purchaseOrder.poDate) === appliedFilters.poDate;
      const matchesDeliveryDate =
        !appliedFilters.deliveryDate ||
        toDateInputValue(purchaseOrder.deliveryDate) === appliedFilters.deliveryDate;
      const matchesBranchWarehouse =
        appliedFilters.branchWarehouse === 'All' ||
        String(purchaseOrder.warehouseLocation || '').trim() ===
          String(appliedFilters.branchWarehouse || '').trim();
      const matchesStatus =
        appliedFilters.statusFilter === 'All' ||
        (appliedFilters.statusFilter === 'Active'
          ? ['Initial', 'In Progress', 'Received'].includes(purchaseOrder.status)
          : purchaseOrder.status === appliedFilters.statusFilter);

      return (
        matchesSearch &&
        matchesPoDate &&
        matchesDeliveryDate &&
        matchesBranchWarehouse &&
        matchesStatus
      );
    });
  }, [appliedFilters, purchaseOrders]);

  const sortedPurchaseOrders = useMemo(
    () =>
      [...filteredPurchaseOrders].sort((firstOrder, secondOrder) => {
        if (deliveryDateSort === 'desc') {
          return compareDateValues(secondOrder.deliveryDate, firstOrder.deliveryDate);
        }

        if (deliveryDateSort === 'asc') {
          return compareDateValues(firstOrder.deliveryDate, secondOrder.deliveryDate);
        }

        return secondOrder.poDate.localeCompare(firstOrder.poDate);
      }),
    [deliveryDateSort, filteredPurchaseOrders]
  );
  const totalPurchaseOrders = sortedPurchaseOrders.length;
  const totalPages = Math.max(1, Math.ceil(totalPurchaseOrders / PURCHASE_ORDER_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * PURCHASE_ORDER_PAGE_SIZE;
  const endIndex = Math.min(startIndex + PURCHASE_ORDER_PAGE_SIZE, totalPurchaseOrders);
  const paginatedPurchaseOrders = useMemo(
    () => sortedPurchaseOrders.slice(startIndex, endIndex),
    [endIndex, sortedPurchaseOrders, startIndex]
  );
  const purchaseOrderSearchSuggestions = useMemo(
    () => buildPurchaseOrderSearchSuggestions(purchaseOrders),
    [purchaseOrders]
  );
  const initialSummarySubmittedPoDates = useMemo(
    () =>
      new Set(
        initialSummaryPurchaseOrders
          .filter((initialSummary) => initialSummary.status === 'Submitted')
          .map((initialSummary) => toDateInputValue(initialSummary.poDate))
          .filter(Boolean)
      ),
    [initialSummaryPurchaseOrders]
  );
  const initialSummaryDraftPoDates = useMemo(
    () =>
      new Set(
        initialSummaryPurchaseOrders
          .filter(
            (initialSummary) =>
              initialSummary.status === 'Draft' &&
              !initialSummarySubmittedPoDates.has(toDateInputValue(initialSummary.poDate))
          )
          .map((initialSummary) => toDateInputValue(initialSummary.poDate))
          .filter(Boolean)
      ),
    [initialSummaryPurchaseOrders, initialSummarySubmittedPoDates]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters]);

  function handleFormChange(event) {
    const { name, value } = event.target;

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
      ...(name === 'supplierId'
        ? { sacksMark: getSupplierSacksMark(value, supplierOptions) }
        : {}),
    }));

    setFormErrors((currentErrors) => ({
      ...currentErrors,
      [name]: '',
      ...(name === 'supplierId' ? { sacksMark: '' } : {}),
    }));
    setSubmitError('');
    setCancelError('');
    setSuccessMessage('');
  }

  function handleFilterChange(event) {
    const { name, value } = event.target;
    const nextSuggestionStatusFilter = getStatusFilterForSearchSuggestion(event.suggestion);

    setFilterValues((currentValues) => ({
      ...currentValues,
      [name]: value,
      ...(nextSuggestionStatusFilter ? { statusFilter: nextSuggestionStatusFilter } : {}),
    }));

    if (['search', 'branchWarehouse', 'poDate', 'deliveryDate'].includes(name)) {
      setAppliedFilters((currentFilters) => ({
        ...currentFilters,
        [name]: value,
        ...(nextSuggestionStatusFilter ? { statusFilter: nextSuggestionStatusFilter } : {}),
      }));
      setCurrentPage(1);
    }
  }

  function handleFilterSubmit(event) {
    event.preventDefault();
    setAppliedFilters(filterValues);
    setCurrentPage(1);
  }

  function handleStatusFilterChange(event) {
    const { value } = event.target;

    setFilterValues((currentValues) => ({
      ...currentValues,
      statusFilter: value,
    }));
    setAppliedFilters((currentFilters) => ({
      ...currentFilters,
      statusFilter: value,
    }));
    setCurrentPage(1);
  }

  function handleFilterReset() {
    const resetValues = createInitialPurchaseOrderFilterValues();

    setFilterValues(resetValues);
    setAppliedFilters(resetValues);
    setCurrentPage(1);
  }

  function handlePreviousPage() {
    setCurrentPage((page) => Math.max(page - 1, 1));
  }

  function handleNextPage() {
    setCurrentPage((page) => Math.min(page + 1, totalPages));
  }

  function handleDeliveryDateSortToggle() {
    setDeliveryDateSort((currentSort) => {
      if (currentSort === 'default') {
        return 'desc';
      }

      if (currentSort === 'desc') {
        return 'asc';
      }

      return 'default';
    });
    setCurrentPage(1);
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const nextErrors = validatePurchaseOrderForm(formValues, {
      requireDeliveryDate: false,
    });

    if (Object.keys(nextErrors).length) {
      setFormErrors(nextErrors);
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const createdPurchaseOrder = await createPurchaseOrder(
        normalizePurchaseOrderPayload(formValues)
      );

      setPurchaseOrders((currentOrders) =>
        dedupePurchaseOrders([createdPurchaseOrder, ...currentOrders])
      );
      resetForm();
      setActiveView('list');
      setSuccessMessage('Purchase order added successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSupplierBatchSubmit(batchValues, options = {}) {
    if (!activeSupplierOptions.length) {
      setSubmitError('Add or activate at least one supplier before submitting supplier batch P.O.s.');
      return false;
    }

    if (!Array.isArray(batchValues.items) || !batchValues.items.length) {
      setSubmitError('Add at least one supplier before submitting supplier batch P.O.s.');
      return false;
    }

    setIsSaving(true);
    setSubmitError('');
    setSuccessMessage('');

    try {
      const savedSummary = initialSummaryPurchaseOrders.find(
        (summary) => toDateInputValue(summary.poDate) === batchValues.poDate
      );
      const savedItems = Array.isArray(savedSummary?.items) ? savedSummary.items : [];
      const nextItems = [];
      const updatedPurchaseOrders = [];
      const activePurchaseOrders = purchaseOrders.filter(
        (purchaseOrder) => purchaseOrder.status !== 'Cancelled'
      );
      const selectedGeneratedIds = new Set();
      const fixedBatchPrice = getFixedBatchPrice(batchValues);
      const batchWarehouseLocation = String(batchValues.warehouseLocation || '').trim();

      for (const item of batchValues.items) {
        const supplier = activeSupplierOptions.find(
          (supplierOption) => String(supplierOption.value) === String(item.supplierId)
        );
        const sacksMark =
          supplier?.sacksMark || item.sacksMark || supplier?.label || item.supplierName || '';
        const existingPurchaseOrder = findInitialSummaryPurchaseOrderForBatchItem(
          activePurchaseOrders,
          batchValues.poDate,
          item,
          savedItems
        );

        if (existingPurchaseOrder) {
          selectedGeneratedIds.add(Number(existingPurchaseOrder.id));
          const normalizedQuantity = Number(item.quantity);
          const canUpdateExisting =
            existingPurchaseOrder.status === 'Initial' &&
            !isPurchaseOrderInSupplierBalancing(supplierBalancings, existingPurchaseOrder.id);
          const hasQuantityChange =
            Number(existingPurchaseOrder.quantity) !== normalizedQuantity;
          const hasFixedPriceChange =
            fixedBatchPrice !== null && Number(existingPurchaseOrder.price) !== fixedBatchPrice;
          const hasWarehouseLocationChange =
            String(existingPurchaseOrder.warehouseLocation || '').trim() !==
            batchWarehouseLocation;

          if (!canUpdateExisting && (hasQuantityChange || hasWarehouseLocationChange)) {
            throw new Error(
              'Generated P.O.s that are already in progress, received, completed, or saved in balancing cannot change sacks quantity or warehouse.'
            );
          }

          if (canUpdateExisting && (hasQuantityChange || hasFixedPriceChange || hasWarehouseLocationChange)) {
            const updatedPurchaseOrder = await updatePurchaseOrder(
              existingPurchaseOrder.id,
              normalizePurchaseOrderPayload({
                ...buildPurchaseOrderEditValues(existingPurchaseOrder),
                quantity: String(normalizedQuantity),
                warehouseLocation: batchWarehouseLocation,
                price:
                  fixedBatchPrice === null
                    ? String(existingPurchaseOrder.price ?? 0)
                    : String(fixedBatchPrice),
                sacksMark,
              })
            );

            updatedPurchaseOrders.push(updatedPurchaseOrder);
          }

          nextItems.push({
            ...item,
            sacksMark,
            purchaseOrderId: existingPurchaseOrder.id,
          });
          continue;
        }

        const createdPurchaseOrder = await createPurchaseOrder(
          buildInitialSummaryPurchaseOrderPayload(batchValues, item, sacksMark, fixedBatchPrice)
        );

        updatedPurchaseOrders.push(createdPurchaseOrder);
        selectedGeneratedIds.add(Number(createdPurchaseOrder.id));
        nextItems.push({
          ...item,
          sacksMark,
          purchaseOrderId: createdPurchaseOrder.id,
        });
      }

      const removedGeneratedPurchaseOrders = savedItems
        .map((item) => findInitialSummaryPurchaseOrderForItem(activePurchaseOrders, batchValues.poDate, item))
        .filter(
          (purchaseOrder) =>
            purchaseOrder &&
            !selectedGeneratedIds.has(Number(purchaseOrder.id)) &&
            !isPurchaseOrderInSupplierBalancing(supplierBalancings, purchaseOrder.id) &&
            ['Initial', 'In Progress'].includes(purchaseOrder.status)
        );

      for (const purchaseOrder of removedGeneratedPurchaseOrders) {
        const cancelledPurchaseOrder = await updatePurchaseOrder(
          purchaseOrder.id,
          normalizePurchaseOrderPayload({
            ...buildPurchaseOrderEditValues(purchaseOrder),
            status: 'Cancelled',
          })
        );

        updatedPurchaseOrders.push(cancelledPurchaseOrder);
      }

      setPurchaseOrders((currentOrders) =>
        dedupePurchaseOrders([...updatedPurchaseOrders, ...currentOrders])
      );
      setInitialSummaryPurchaseOrders((currentSummaries) => {
        const nextSummary = {
          poDate: batchValues.poDate,
          status: 'Submitted',
          priceMin: batchValues.priceMin || null,
          priceMax: batchValues.priceMax || null,
          estimatedFclQuantity: batchValues.estimatedFclQuantity || null,
          items: nextItems,
        };

        return [
          nextSummary,
          ...currentSummaries.filter(
            (summary) => toDateInputValue(summary.poDate) !== batchValues.poDate
          ),
        ];
      });
      if (!options.stayOnSupplierBatch) {
        setActiveView('list');
      }
      const createdCount = updatedPurchaseOrders.filter(
        (purchaseOrder) => purchaseOrder.status !== 'Cancelled'
      ).length;
      setSuccessMessage(
        `${createdCount} supplier batch P.O.${
          createdCount === 1 ? '' : 's'
        } submitted successfully.`
      );
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleCancelPurchaseOrder(purchaseOrder) {
    if (!purchaseOrder || !['Initial', 'In Progress'].includes(purchaseOrder.status)) {
      return false;
    }

    setCancelingPurchaseOrderId(purchaseOrder.id);
    setCancelError('');
    setSuccessMessage('');

    try {
      const updatedPurchaseOrder = await updatePurchaseOrder(
        purchaseOrder.id,
        normalizePurchaseOrderPayload({
          ...buildPurchaseOrderEditValues(purchaseOrder),
          status: 'Cancelled',
        })
      );

      setPurchaseOrders((currentOrders) =>
        currentOrders.map((currentOrder) =>
          currentOrder.id === purchaseOrder.id ? updatedPurchaseOrder : currentOrder
        )
      );
      setSuccessMessage('Purchase order cancelled successfully.');
      return true;
    } catch (error) {
      setCancelError(error.message);
      return false;
    } finally {
      setCancelingPurchaseOrderId(null);
    }
  }

  async function handleSupplierBalancingSubmit(payload) {
    setIsSaving(true);
    setSubmitError('');
    setSuccessMessage('');

    try {
      const createdBalancing = await createSupplierBalancing(payload);
      setSupplierBalancings((currentBalancings) => [
        createdBalancing,
        ...currentBalancings,
      ]);
      setSuccessMessage('Supplier balancing saved successfully.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSupplierBalancingStatusChange(balancing, status) {
    if (!balancing?.id) {
      return false;
    }

    if (
      balancing.status === 'Paid' &&
      status !== 'Paid' &&
      isSupplierBalancingLockedByCompletedPurchaseOrder(balancing, purchaseOrders)
    ) {
      setSubmitError(
        'This balancing record is capital locked because one or more included P.O.s are already Completed.'
      );
      return false;
    }

    setIsSaving(true);
    setSubmitError('');
    setSuccessMessage('');

    try {
      const updatedBalancing = await updateSupplierBalancingStatus(
        balancing.id,
        status
      );
      setSupplierBalancings((currentBalancings) =>
        currentBalancings.map((currentBalancing) =>
          currentBalancing.id === updatedBalancing.id
            ? updatedBalancing
            : currentBalancing
        )
      );
      setSuccessMessage('Supplier balancing status updated.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSupplierBalancingEdit(balancing) {
    if (!balancing?.id) {
      return false;
    }

    if (isSupplierBalancingLockedByCompletedPurchaseOrder(balancing, purchaseOrders)) {
      setSubmitError(
        'This balancing record is capital locked because one or more included P.O.s are already Completed.'
      );
      return false;
    }

    setIsSaving(true);
    setSubmitError('');
    setSuccessMessage('');

    try {
      await deleteSupplierBalancing(balancing.id);
      setSupplierBalancings((currentBalancings) =>
        currentBalancings.filter((currentBalancing) => currentBalancing.id !== balancing.id)
      );
      setSuccessMessage('Supplier balancing loaded for editing.');
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() {
    resetForm();
    setActiveView('list');
  }

  function handleInitialSummaryDraftSaved(message) {
    setSubmitError('');
    setSuccessMessage(message || 'Initial Summary P.O. draft saved.');
  }

  function handleEditChange(event) {
    purchaseOrderEdit.handleEditChange(event);
  }

  async function handleEditSubmit(event) {
    return purchaseOrderEdit.handleEditSubmit(event);
  }

  function resetForm() {
    resetPurchaseOrderFormState(setFormValues, setFormErrors, setSubmitError);
  }

  return {
    activeView,
    filterValues,
    formValues,
    formErrors,
    purchaseOrders: paginatedPurchaseOrders,
    allPurchaseOrders: purchaseOrders,
    deliveryDateSort,
    purchaseOrderPagination: {
      currentPage: normalizedPage,
      endItem: endIndex,
      isNextDisabled: normalizedPage >= totalPages,
      isPreviousDisabled: normalizedPage <= 1,
      onNext: handleNextPage,
      onPrevious: handlePreviousPage,
      startItem: totalPurchaseOrders ? startIndex + 1 : 0,
      totalItems: totalPurchaseOrders,
      totalPages,
    },
    purchaseOrderSearchSuggestions,
    initialSummarySubmittedPoDates,
    initialSummaryDraftPoDates,
    supplierBalancings,
    supplierOptions,
    activeSupplierOptions,
    seaweedTypeOptions,
    warehouseLocationOptions,
    lansaOptions,
    hasSuppliers,
    hasSeaweedTypes,
    hasWarehouseLocations,
    hasLansas,
    isLoading,
    isSaving,
    cancelingPurchaseOrderId,
    loadError,
    submitError,
    cancelError,
    successMessage,
    editingPurchaseOrder: purchaseOrderEdit.editingPurchaseOrder,
    editValues: purchaseOrderEdit.editValues,
    editErrors: purchaseOrderEdit.editErrors,
    editError: purchaseOrderEdit.editError,
    isEditSaving: purchaseOrderEdit.isEditSaving,
    progressingPurchaseOrder: purchaseOrderProgress.progressingPurchaseOrder,
    progressValues: purchaseOrderProgress.progressValues,
    receivingEditValues: purchaseOrderProgress.receivingEditValues,
    initialReceivingEditValues: purchaseOrderProgress.initialReceivingEditValues,
    isProgressSaving: purchaseOrderProgress.isProgressSaving,
    isReceivingSaving: purchaseOrderProgress.isReceivingSaving,
    progressError: purchaseOrderProgress.progressError,
    historyPurchaseOrder: purchaseOrderHistory.historyPurchaseOrder,
    historyLogs: purchaseOrderHistory.historyLogs,
    isHistoryLoading: purchaseOrderHistory.isHistoryLoading,
    historyError: purchaseOrderHistory.historyError,
    setActiveView,
    handleFilterChange,
    handleFilterSubmit,
    handleStatusFilterChange,
    handleFilterReset,
    handleDeliveryDateSortToggle,
    handleFormChange,
    handleSubmit,
    handleSupplierBatchSubmit,
    handleSupplierBalancingSubmit,
    handleSupplierBalancingEdit,
    handleSupplierBalancingStatusChange,
    handleInitialSummaryDraftSaved,
    handleCancel,
    handleCancelPurchaseOrder,
    openEditModal: purchaseOrderEdit.openEditModal,
    closeEditModal: purchaseOrderEdit.closeEditModal,
    handleEditChange,
    handleEditSubmit,
    openProgressModal: purchaseOrderProgress.openProgressModal,
    closeProgressModal: purchaseOrderProgress.closeProgressModal,
    handleProgressChange: purchaseOrderProgress.handleProgressChange,
    resetProgressValues: purchaseOrderProgress.resetProgressValues,
    resetReceivingEditValues: purchaseOrderProgress.resetReceivingEditValues,
    handleProgressSubmit: purchaseOrderProgress.handleProgressSubmit,
    handleFinalizeForBalancing: purchaseOrderProgress.handleFinalizeForBalancing,
    handleReopenReceivingForBalancing: purchaseOrderProgress.handleReopenReceivingForBalancing,
    handleReceivingEditChange: purchaseOrderProgress.handleReceivingEditChange,
    handleReceivingEditSubmit: purchaseOrderProgress.handleReceivingEditSubmit,
    openHistoryModal: purchaseOrderHistory.openHistoryModal,
    closeHistoryModal: purchaseOrderHistory.closeHistoryModal,
  };
}

function createInitialPurchaseOrderFilterValues() {
  return {
    search: '',
    branchWarehouse: 'All',
    poDate: '',
    deliveryDate: '',
    statusFilter: DEFAULT_PURCHASE_ORDER_STATUS_FILTER,
  };
}

function compareDateValues(firstValue, secondValue) {
  return String(firstValue || '').localeCompare(String(secondValue || ''));
}

function toDateInputValue(value) {
  if (!value) {
    return '';
  }

  return String(value).slice(0, 10);
}

function dedupePurchaseOrders(purchaseOrders = []) {
  const seenKeys = new Set();

  return purchaseOrders.filter((purchaseOrder) => {
    const poCode = String(purchaseOrder?.poCode || '').trim();
    const identity = poCode
      ? `code:${poCode.toLowerCase()}`
      : `id:${purchaseOrder?.id || ''}`;

    if (!identity || seenKeys.has(identity)) {
      return false;
    }

    seenKeys.add(identity);
    return true;
  });
}

function getPurchaseOrderSearchableCode(purchaseOrder) {
  if (purchaseOrder.poCode) {
    return String(purchaseOrder.poCode);
  }

  return `PO-${String(purchaseOrder.id).padStart(4, '0')}`;
}

function getStatusFilterForSearchSuggestion(suggestion) {
  if (suggestion?.group === 'supplier') {
    return DEFAULT_PURCHASE_ORDER_STATUS_FILTER;
  }

  const status = suggestion?.status;

  if (!status) {
    return '';
  }

  if (['Initial', 'In Progress', 'Received'].includes(status)) {
    return 'Active';
  }

  return status;
}

function buildPurchaseOrderSearchSuggestions(purchaseOrders) {
  const suggestionsByKey = new Map();

  purchaseOrders.forEach((purchaseOrder) => {
    const poCode = getPurchaseOrderSearchableCode(purchaseOrder);
    const supplierName = String(purchaseOrder.supplierName || '').trim();

    if (poCode) {
      suggestionsByKey.set(`po-code:${poCode.toLowerCase()}`, {
        group: 'po-code',
        value: poCode,
        label: poCode,
        detail: supplierName ? `P.O. Code - ${supplierName}` : 'P.O. Code',
        status: purchaseOrder.status || '',
      });
    }

    if (supplierName) {
      suggestionsByKey.set(`supplier:${supplierName.toLowerCase()}`, {
        group: 'supplier',
        value: supplierName,
        label: supplierName,
        detail: 'Supplier',
        status: purchaseOrder.status || '',
      });
    }
  });

  return Array.from(suggestionsByKey.values()).sort((firstSuggestion, secondSuggestion) =>
    firstSuggestion.label.localeCompare(secondSuggestion.label)
  );
}

function buildInitialSummaryPurchaseOrderPayload(batchValues, item, sacksMark, fixedBatchPrice = null) {
  return {
    poDate: batchValues.poDate,
    supplierId: Number(item.supplierId),
    quantity: Number(item.quantity),
    price: fixedBatchPrice === null ? 0 : fixedBatchPrice,
    warehouseLocation: String(batchValues.warehouseLocation || '').trim(),
    actualPrice: null,
    receivedQuantity: null,
    stockUnit: 'sacks',
    lansaName: '',
    sacksMark,
    deliveryDate: null,
    seaweedType: '',
    status: 'Initial',
  };
}

function getFixedBatchPrice(batchValues) {
  const priceMinText = String(batchValues?.priceMin || '').trim();
  const priceMaxText = String(batchValues?.priceMax || '').trim();

  if (!priceMinText || !priceMaxText) {
    return null;
  }

  const priceMin = Number(priceMinText);
  const priceMax = Number(priceMaxText);

  if (!Number.isFinite(priceMin) || !Number.isFinite(priceMax) || priceMin !== priceMax) {
    return null;
  }

  return priceMin;
}

function findInitialSummaryPurchaseOrderForItem(purchaseOrders, poDate, item) {
  const purchaseOrderId = Number(item?.purchaseOrderId);

  if (Number.isInteger(purchaseOrderId) && purchaseOrderId > 0) {
    const matchedById = purchaseOrders.find(
      (purchaseOrder) => Number(purchaseOrder.id) === purchaseOrderId
    );

    if (matchedById) {
      return matchedById;
    }
  }

  const normalizedPoDate = toDateInputValue(poDate);
  const normalizedSupplierId = String(item?.supplierId || '');

  return purchaseOrders.find(
    (purchaseOrder) =>
      toDateInputValue(purchaseOrder.poDate) === normalizedPoDate &&
      String(purchaseOrder.supplierId) === normalizedSupplierId &&
      purchaseOrder.stockUnit === 'sacks'
  );
}

function findInitialSummaryPurchaseOrderForBatchItem(purchaseOrders, poDate, item, savedItems) {
  const purchaseOrderId = Number(item?.purchaseOrderId);

  if (Number.isInteger(purchaseOrderId) && purchaseOrderId > 0) {
    return findInitialSummaryPurchaseOrderForItem(purchaseOrders, poDate, item);
  }

  const savedItem = (savedItems || []).find(
    (currentItem) => String(currentItem.supplierId) === String(item?.supplierId)
  );

  if (!savedItem) {
    return null;
  }

  return findInitialSummaryPurchaseOrderForItem(purchaseOrders, poDate, savedItem);
}

function isPurchaseOrderInSupplierBalancing(supplierBalancings, purchaseOrderId) {
  const normalizedPurchaseOrderId = String(purchaseOrderId || '');

  if (!normalizedPurchaseOrderId) {
    return false;
  }

  return supplierBalancings.some((record) =>
    (record.purchaseOrderIds || []).some(
      (recordPurchaseOrderId) => String(recordPurchaseOrderId) === normalizedPurchaseOrderId
    )
  );
}

function isSupplierBalancingLockedByCompletedPurchaseOrder(balancing, purchaseOrders = []) {
  const includedPurchaseOrderIds = new Set(
    (balancing?.purchaseOrderIds || []).map((purchaseOrderId) => String(purchaseOrderId))
  );

  if (!includedPurchaseOrderIds.size) {
    return false;
  }

  return purchaseOrders.some(
    (purchaseOrder) =>
      includedPurchaseOrderIds.has(String(purchaseOrder.id)) &&
      purchaseOrder.status === 'Completed'
  );
}

export default usePurchaseOrders;
